// Platform / browser capability probes, as pure predicates.
//
// These used to live inside the feature modules as `style.display = 'none'`
// after the button had already been rendered (narrator.ts:34, voiceCommands.ts:51,
// tiltControls.ts:69). Hoisting them into the registry means an unsupported
// feature never occupies a rail slot in the first place, which matters when the
// phone layout only has two.

export function hasSpeechSynthesis(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Can this browser turn speech into a command AT ALL?
 *
 * Not just the Web Speech API. Safari — every iPhone — ships speechSynthesis
 * but not SpeechRecognition, so gating on the native API alone hid the voice
 * button on the most common device this product is opened on, while the mic
 * path that does work there sat wired only into the VR grip-to-talk flow.
 *
 * Second clause = CloudVoiceInput's requirements: getUserMedia + MediaRecorder,
 * both supported on iOS for years. Kept in sync with
 * VoiceCommands.isSupported(), which is what actually decides at runtime.
 */
export function hasVoiceInput(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as Record<string, unknown>;
  if ('SpeechRecognition' in w || 'webkitSpeechRecognition' in w) return true;
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

/**
 * Does the tour carry the geometry these draw FROM?
 *
 * The floor plan and the dollhouse render `room.floorplan.shape` polygons. A
 * tour built through the portal has none — nothing in that path runs
 * `geometry:estimate` — so both buttons toggled state and drew nothing.
 * Measured on quadrimoveis-3q: 9 rooms, zero with a floorplan. 🗺 is the FIRST
 * rail button on a phone, so the most prominent control in the product did
 * nothing at all on a live client tour.
 *
 * A button that cannot work should not be offered; the registry gate is where
 * that decision belongs.
 */
export function hasFloorplanGeometry(rooms: readonly { floorplan?: { shape?: unknown } }[]): boolean {
  return rooms.some((r) => Array.isArray(r.floorplan?.shape) && r.floorplan.shape.length >= 3);
}

/**
 * Whether the #minimap panel draws ANYTHING — which is not the same question as
 * `hasFloorplanGeometry`, and conflating the two is what broke the gallery demo.
 *
 * `Floorplan.render()` has two branches: the SVG plan (needs polygons) and
 * `renderLabelGrid()`, a list of room buttons, used whenever no room carries a
 * `floorplan`. The dollhouse has no such fallback — with no geometry it draws
 * an empty viewport, which is why its DEFAULT is geometry-aware
 * (`App.dollhouseDefaultVisible`). Its toggle is a separate question: see
 * `hasDollhouseToggle`.
 *
 * Gating the FLOOR PLAN on geometry alone removed its toggle from every
 * portal-built tour while the panel kept rendering the room list. That is worse
 * than the bug it fixed: `App.installFloorplanToggle()` early-returns when
 * `#floorplan-toggle` is absent, so the saved `imersivo:floorplanVisible`
 * preference was never applied and the list sat permanently open, covering the
 * panorama on a phone with no way to close it (reported on Lambert Fine Arts).
 *
 * One room needs no list to navigate between, so two is the floor.
 */
export function hasRoomPanelContent(
  rooms: readonly { floorplan?: { shape?: unknown } }[],
): boolean {
  return hasFloorplanGeometry(rooms) || rooms.length >= 2;
}

/**
 * Whether to offer the dollhouse TOGGLE — which is not the same question as
 * whether the dollhouse has anything to draw.
 *
 * Gating this on `hasFloorplanGeometry` was the same mistake the floor plan
 * made one fix earlier, and it landed harder. The dollhouse is a second camera
 * that paints over the panorama from the moment it is constructed; the ONLY
 * thing that turned it off at boot was `App.installDollhouseToggle`, which
 * early-returns when `#dollhouse-toggle` is missing. So on every portal-built
 * tour — none of which run `geometry:estimate` — withholding the button did not
 * withhold the widget. It painted an empty grey viewport over the top-right of
 * the panorama, on the first visit, with no button and no `M` key to close it.
 * Measured on Lambert Fine Arts: 11 rooms, 0 with geometry, `is-hidden` absent
 * on a fresh load.
 *
 * A control that dismisses something the visitor can see must exist whenever
 * that thing can be seen. What geometry decides is the DEFAULT, not the offer.
 *
 * One room needs no maquete to move between, so two is the floor — same as the
 * room panel.
 */
export function hasDollhouseToggle(rooms: readonly unknown[]): boolean {
  return rooms.length >= 2;
}

export function hasDeviceOrientation(): boolean {
  if (typeof window === 'undefined') return false;
  return 'DeviceOrientationEvent' in window && isCoarsePointer();
}

/** Touch-first device. Used for tilt, and for swapping hover affordances. */
export function isCoarsePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

export function hasClipboard(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;
}

/** Copresence needs a Pusher key baked in at build time; Vite inlines VITE_*.
 *  Cast through the same shape the rest of the repo uses (apiClient.ts:16,
 *  tourEmbed.ts:72) so the module stays importable where import.meta.env is
 *  absent — the Node test runner, for one. */
export function hasCopresence(): boolean {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
  return !!env.VITE_PUSHER_KEY;
}
